import {formatReais} from "../../shared/utils/formatters";

/*
 * Regras das telas de métricas (BBF-167 a), como o protótipo v2 (`telas-metricas.js` e `core.js`) e o
 * app-funcionalidades da BBF-149 e da BBF-153:
 *  - a métrica é global: como mede, o que faz ao atingir o limite e como cobra;
 *  - natureza, apuração, forma de cobrança e tamanho do bloco travam quando ela está numa versão publicada;
 *  - o sistema aponta as que usa, dizendo de onde vem o número e se consegue bloquear;
 *  - o excedente vai sempre para a fatura seguinte.
 * Quem decide é a API; aqui ficam os rótulos, o formulário e o exemplo da fatura.
 */

export const NATUREZAS = {
    ESTADO: {label: 'Estado', texto: 'Quantidade num instante: usuários, projetos, vidas.'},
    CONSUMO: {label: 'Consumo', texto: 'Acumula no período: tokens, execuções.'},
};

export const APURACOES = {
    PICO: {label: 'Pico do ciclo', texto: 'Maior quantidade vista no ciclo.'},
    MEDIA: {label: 'Média diária', texto: 'Média das quantidades de cada dia do ciclo.'},
    FECHAMENTO: {label: 'Fechamento', texto: 'Quantidade no último dia do ciclo.'},
    SOMA: {label: 'Soma', texto: 'Total acumulado no ciclo.'},
};

export const AO_ATINGIR = {
    BLOQUEAR: {label: 'Bloquear', texto: 'O sistema impede passar do limite.'},
    LIBERAR: {label: 'Liberar', texto: 'Deixa passar e cobra o excedente, se houver preço.'},
};

export const FORMAS = {
    UNIDADE: {label: 'Por unidade', texto: 'Excedente × preço da unidade.'},
    BLOCO: {label: 'Por bloco', texto: 'Blocos arredondados para cima × preço do bloco.'},
    FAIXAS: {label: 'Por faixas', texto: 'Escalonado sobre o total. A primeira faixa com preço 0 é a franquia.'},
    NAO_COBRAR: {label: 'Não cobrar', texto: 'Só mede, para acompanhamento.'},
};

export const FONTES = {
    BACKOFFICE: {label: 'Backoffice', texto: 'O próprio backoffice conta, sem depender do sistema.'},
    EVENTOS: {label: 'Eventos', texto: 'O sistema envia cada ocorrência, e o backoffice soma.'},
    FOTO: {label: 'Foto diária', texto: 'O sistema envia a quantidade do dia, uma vez por dia.'},
};

export const AVISOS = [50, 80, 90, 100];

// Só esta o backoffice mede sozinho, pelos vínculos do identity.
export const CHAVE_USUARIOS = 'usuarios';

const FORMATO_DA_CHAVE = /^[a-z][a-z0-9_]*$/;

export const rotulo = (tabela, valor) => tabela[valor]?.label || valor || '-';

export const plural = (n, um, varios) => `${fmt(n)} ${Number(n) === 1 ? um : varios}`;

export const fmt = (n) => new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 2}).format(Number(n) || 0);

const numero = (valor) => {
    const n = Number(valor);
    return valor === '' || valor == null || Number.isNaN(n) ? 0 : n;
};

const vazio = (valor) => valor === '' || valor == null;

/* ---------- formulário ---------- */

export const metricaVazia = () => ({
    chave: '',
    nome: '',
    descricao: '',
    unidade_singular: '',
    unidade_plural: '',
    natureza: 'ESTADO',
    apuracao: 'PICO',
    ao_atingir: 'BLOQUEAR',
    avisos: [80, 100],
    permite_teto: false,
    forma_cobranca: 'UNIDADE',
    bloco_tamanho: '',
    bloco_nome: '',
});

export const formDaMetrica = (metrica) => ({
    chave: metrica.chave || '',
    nome: metrica.nome || '',
    descricao: metrica.descricao || '',
    unidade_singular: metrica.unidade_singular || '',
    unidade_plural: metrica.unidade_plural || '',
    natureza: metrica.natureza || 'ESTADO',
    apuracao: metrica.apuracao || 'PICO',
    ao_atingir: metrica.ao_atingir || 'BLOQUEAR',
    avisos: [...(metrica.avisos || [])].sort((a, b) => a - b),
    permite_teto: Boolean(metrica.permite_teto),
    forma_cobranca: metrica.forma_cobranca || 'UNIDADE',
    bloco_tamanho: metrica.bloco_tamanho == null ? '' : String(metrica.bloco_tamanho),
    bloco_nome: metrica.bloco_nome || '',
});

// Consumo só soma, e estado não soma; fora do bloco, tamanho e nome do bloco saem.
export const escolher = (form, campo, valor) => {
    const novo = {...form, [campo]: valor};
    if (campo === 'natureza') {
        if (valor === 'CONSUMO') novo.apuracao = 'SOMA';
        else if (novo.apuracao === 'SOMA') novo.apuracao = 'PICO';
    }
    if (campo === 'forma_cobranca' && valor !== 'BLOCO') {
        novo.bloco_tamanho = '';
        novo.bloco_nome = '';
    }
    return novo;
};

export const apuracaoPermitida = (natureza, apuracao) => (natureza === 'CONSUMO' ? apuracao === 'SOMA' : apuracao !== 'SOMA');

export const alternarAviso = (form, aviso) => ({
    ...form,
    avisos: form.avisos.includes(aviso)
        ? form.avisos.filter((item) => item !== aviso)
        : [...form.avisos, aviso].sort((a, b) => a - b),
});

// O que a tela confere antes de mandar. A API confere de novo e responde com a regra.
export const validarMetrica = (form, novo) => {
    const chave = form.chave.trim();
    if (novo && !FORMATO_DA_CHAVE.test(chave)) return 'A chave usa só letras minúsculas, números e sublinhado, começando por letra.';
    if (novo && chave === 'nova') return 'A chave "nova" é reservada. Escolha outra.';
    if (!form.nome.trim()) return 'Dê um nome à métrica.';
    if (!form.unidade_singular.trim() || !form.unidade_plural.trim()) {
        return 'Informe a unidade no singular e no plural. É assim que ela aparece na fatura.';
    }
    if (form.forma_cobranca === 'BLOCO') {
        const tamanho = Number(form.bloco_tamanho);
        if (!Number.isInteger(tamanho) || tamanho < 2) return 'Na cobrança por bloco, o bloco tem pelo menos 2 unidades. Para 1, use "por unidade".';
        if (!form.bloco_nome.trim()) return 'Dê um nome ao bloco, como "milhão de tokens".';
    }
    return '';
};

// O corpo do POST (com a chave) ou do PUT, que é parcial: a chave não muda e fica de fora.
export const corpoDaMetrica = (form, novo) => {
    const bloco = form.forma_cobranca === 'BLOCO';
    return {
        ...(novo ? {chave: form.chave.trim()} : {}),
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        unidade_singular: form.unidade_singular.trim(),
        unidade_plural: form.unidade_plural.trim(),
        natureza: form.natureza,
        apuracao: form.apuracao,
        ao_atingir: form.ao_atingir,
        avisos: form.avisos,
        permite_teto: form.permite_teto,
        forma_cobranca: form.forma_cobranca,
        bloco_tamanho: bloco ? Number(form.bloco_tamanho) : null,
        bloco_nome: bloco ? form.bloco_nome.trim() : null,
    };
};

export const metricaAlterada = (form, metrica) => (
    JSON.stringify(corpoDaMetrica(form, false)) !== JSON.stringify(corpoDaMetrica(formDaMetrica(metrica), false))
);

/* ---------- onde a métrica é usada ---------- */

export const versaoPublicada = (versao) => Boolean(versao?.situacao) && versao.situacao !== 'RASCUNHO';

export const planosQueUsam = (metrica) => new Set((metrica?.versoes || []).map((versao) => versao.id_plano)).size;

export const nomeDaVersao = (versao, sistemas = []) => {
    const sistema = sistemas.find((item) => item.id === versao.id_sistema);
    return `${sistema ? `${sistema.nome} ` : ''}${versao.nome_plano || 'Plano'} v${versao.numero}`;
};

/* ---------- quantidades e limites, como o protótipo escreve ---------- */

// A unidade que o preço multiplica: o bloco, na cobrança por bloco, ou a unidade.
export const unidadeDeCobranca = (metrica) => (
    metrica.forma_cobranca === 'BLOCO'
        ? (metrica.bloco_nome || `bloco de ${fmt(metrica.bloco_tamanho)} ${metrica.unidade_plural}`)
        : metrica.unidade_singular
);

// Quantidade por extenso: 30.000.000 vira "30 milhões de tokens".
export const quantidade = (metrica, n) => {
    const valor = Number(n) || 0;
    if (Math.abs(valor) >= 1e6) {
        const milhoes = valor / 1e6;
        return `${fmt(milhoes)}${Math.abs(milhoes) < 2 ? ' milhão' : ' milhões'} de ${metrica.unidade_plural}`;
    }
    return plural(valor, metrica.unidade_singular, metrica.unidade_plural);
};

// Só o número, para não repetir a unidade: "5 milhões", "15".
export const quantidadeCurta = (n) => {
    const valor = Number(n) || 0;
    if (Math.abs(valor) >= 1e6) return `${fmt(valor / 1e6)}${Math.abs(valor / 1e6) < 2 ? ' milhão' : ' milhões'}`;
    return fmt(valor);
};

const regraDoLimite = (metrica, limite) => limite?.ao_atingir || metrica.ao_atingir;

export const franquiaDoLimite = (metrica, limite) => {
    if (!limite) return null;
    if (metrica.forma_cobranca === 'FAIXAS') {
        const primeira = (limite.faixas || [])[0];
        if (!primeira || numero(primeira.preco)) return 0;
        return vazio(primeira.ate) ? null : Number(primeira.ate);
    }
    return vazio(limite.quantidade) ? null : Number(limite.quantidade);
};

// Limite e valor de uma métrica numa versão, em uma linha de texto (`M.resumoLimite`).
export const resumoDoLimite = (metrica, limite) => {
    if (!limite) return 'Não definida nesta versão: sem limite e sem cobrança.';
    const forma = metrica.forma_cobranca;
    const regra = regraDoLimite(metrica, limite);
    const padrao = limite.ao_atingir && limite.ao_atingir !== metrica.ao_atingir ? '' : ' (padrão)';
    if (forma === 'FAIXAS') {
        let anterior = 0;
        const faixas = (limite.faixas || []).map((faixa) => {
            const texto = vazio(faixa.ate) ? `acima de ${fmt(anterior)}` : `até ${fmt(faixa.ate)}`;
            anterior = faixa.ate;
            return `${texto}: ${numero(faixa.preco) ? formatReais(faixa.preco) : 'franquia'}`;
        });
        return `${faixas.join(' · ')} por ${metrica.unidade_singular}${regra === 'BLOQUEAR' ? ' · bloqueia na franquia' : ''}`;
    }
    const q = vazio(limite.quantidade) ? null : Number(limite.quantidade);
    const nomeDaRegra = rotulo(AO_ATINGIR, regra).toLowerCase();
    if (forma === 'NAO_COBRAR') {
        return `Só medida, sem cobrança${q != null ? ` · limite de ${quantidade(metrica, q)} · ${nomeDaRegra}${padrao}` : ''}.`;
    }
    const troca = padrao ? '' : ` (o padrão é ${rotulo(AO_ATINGIR, metrica.ao_atingir).toLowerCase()})`;
    let texto = `${q != null ? quantidade(metrica, q) : 'Sem quantidade'} · ${nomeDaRegra}${padrao}${troca}`;
    if (regra === 'LIBERAR') {
        texto += numero(limite.preco) ? ` · ${formatReais(limite.preco)} por ${unidadeDeCobranca(metrica)} excedente` : ' · excedente sem preço';
    }
    if (regra === 'LIBERAR' && numero(limite.teto) && metrica.permite_teto) texto += ` · teto ${fmt(limite.teto)}×`;
    return `${texto}.`;
};

/*
 * A linha da fatura de uma métrica a partir do apurado no ciclo (`M.calcular`). Serve só ao exemplo do cadastro, que
 * mostra uma métrica ainda não salva: a fatura de verdade e o simulador são calculados pelo gerar-fatura (BBF-153).
 * Devolve {texto, aviso, valor, proxima, efetivo}; `proxima` é o excedente, que vai para a fatura seguinte.
 */
export const calcularLinha = (metrica, apontamento, limite, apurado) => {
    const u = (n) => quantidade(metrica, n);
    const c = (n) => quantidadeCurta(n);
    const forma = metrica.forma_cobranca;
    const bloqueia = Boolean(apontamento?.bloqueia);
    const r = {texto: '', aviso: '', valor: 0, proxima: false, efetivo: apurado};
    if (!limite) {
        r.texto = `Medição: ${u(apurado)}. Não definida nesta versão: sem limite e sem cobrança.`;
    } else if (forma === 'NAO_COBRAR') {
        r.texto = `Medição: ${u(apurado)}, sem cobrança.`;
        const q = franquiaDoLimite(metrica, limite);
        if (q != null && regraDoLimite(metrica, limite) === 'BLOQUEAR' && apurado > q && bloqueia) {
            r.efetivo = q;
            r.texto = `Bloqueado em ${u(q)}, sem cobrança.`;
        }
    } else if (forma === 'FAIXAS') {
        const franquia = franquiaDoLimite(metrica, limite);
        let efetivo = apurado;
        if (regraDoLimite(metrica, limite) === 'BLOQUEAR' && franquia != null && apurado > franquia) {
            if (bloqueia) {
                efetivo = franquia;
                r.aviso = `Bloqueado em ${u(franquia)}: o sistema impediu passar da franquia.`;
            } else {
                r.aviso = 'A regra é bloquear, mas o sistema não consegue bloquear esta métrica.';
            }
        }
        let anterior = 0;
        const partes = [];
        (limite.faixas || []).forEach((faixa) => {
            if (anterior === Infinity || efetivo <= anterior) return;
            const ate = vazio(faixa.ate) ? Infinity : Number(faixa.ate);
            const n = Math.max(0, Math.min(efetivo, ate) - anterior);
            const preco = numero(faixa.preco);
            if (n > 0) {
                r.valor += n * preco;
                partes.push(preco ? `${fmt(n)} × ${formatReais(preco)}` : `${fmt(n)} na franquia`);
            }
            anterior = ate;
        });
        if (efetivo > anterior) partes.push(`${fmt(efetivo - anterior)} acima da última faixa, sem preço`);
        r.efetivo = efetivo;
        r.texto = `${u(efetivo)}${partes.length ? `: ${partes.join(' + ')}.` : '.'}`;
        r.proxima = r.valor > 0;
    } else {
        const q = numero(limite.quantidade);
        let efetivo = apurado;
        if (regraDoLimite(metrica, limite) === 'BLOQUEAR') {
            if (apurado > q && bloqueia) {
                efetivo = q;
                r.texto = `Bloqueado em ${u(q)}, sem cobrança. O sistema impediu passar do limite.`;
            } else if (apurado > q) {
                r.texto = `${u(apurado)}, limite de ${c(q)}, sem cobrança.`;
                r.aviso = 'A regra é bloquear, mas o sistema não consegue bloquear esta métrica: passou do limite.';
            } else {
                r.texto = `${u(apurado)}, dentro do limite de ${c(q)}.`;
            }
        } else {
            const teto = numero(limite.teto);
            if (teto > 0 && metrica.permite_teto && bloqueia && apurado > q * teto) {
                efetivo = q * teto;
                r.aviso = `O consumo parou no teto de ${c(q * teto)} (${fmt(teto)}× a franquia). O sistema bloqueou os outros ${c(apurado - efetivo)}.`;
            }
            const excedente = Math.max(0, efetivo - q);
            const preco = numero(limite.preco);
            if (!excedente) {
                r.texto = `${u(efetivo)}, dentro da franquia de ${c(q)}.`;
            } else if (!preco) {
                r.texto = `${u(efetivo)}, franquia de ${c(q)}. Excedente de ${c(excedente)} liberado, sem preço definido: sem cobrança.`;
            } else if (forma === 'BLOCO') {
                const blocos = Math.ceil(excedente / (Number(metrica.bloco_tamanho) || 1));
                r.valor = blocos * preco;
                r.texto = `${u(efetivo)}, franquia de ${c(q)}. Excedente de ${c(excedente)} = ${plural(blocos, 'bloco', 'blocos')} × ${formatReais(preco)} por ${unidadeDeCobranca(metrica)}.`;
            } else {
                r.valor = excedente * preco;
                r.texto = `${u(efetivo)}, franquia de ${c(q)}. Excedente de ${c(excedente)} × ${formatReais(preco)} por ${metrica.unidade_singular}.`;
            }
            r.proxima = r.valor > 0;
        }
        r.efetivo = efetivo;
    }
    return r;
};

// Os valores do plano só servem ao exemplo se tiverem a forma de cobrança que o formulário escolheu.
const limiteServe = (forma, limite) => Boolean(limite) && (
    forma === 'FAIXAS' ? Boolean(limite.faixas?.length) : forma === 'NAO_COBRAR' || !vazio(limite.quantidade)
);

const MEDICAO = {
    PICO: 'Vale a maior quantidade vista no ciclo.',
    MEDIA: 'Vale a média das quantidades de cada dia do ciclo.',
    FECHAMENTO: 'Vale a quantidade no último dia do ciclo.',
};

/*
 * O exemplo "Como aparece na fatura" do cadastro: com os valores da primeira versão publicada que usa a métrica (ou
 * da primeira versão), se tiverem a forma escolhida; senão, com valores fictícios. `referencia` é {nome, limite}.
 */
export const exemploDaFatura = (form, referencia) => {
    const forma = form.forma_cobranca;
    const tamanho = Number(form.bloco_tamanho) || 1;
    const serve = limiteServe(forma, referencia?.limite);
    let limite;
    if (serve) {
        limite = {...referencia.limite, ao_atingir: null};
    } else if (forma === 'FAIXAS') {
        limite = {faixas: [{ate: 1000, preco: 0}, {ate: 5000, preco: 1}, {ate: null, preco: 0.5}]};
    } else {
        limite = {quantidade: forma === 'BLOCO' ? tamanho * 5 : 10, preco: 10};
    }
    let apurado = 120;
    if (forma === 'FAIXAS') {
        const finitas = limite.faixas.filter((faixa) => !vazio(faixa.ate));
        apurado = finitas.length ? Math.round(Number(finitas[finitas.length - 1].ate) * 1.24) : 1500;
    } else if (forma === 'BLOCO') {
        apurado = Number(limite.quantidade) + Math.round(tamanho * 2.4);
    } else if (forma === 'UNIDADE') {
        apurado = Math.ceil(Number(limite.quantidade) * 1.2) || 3;
    }
    const metrica = {
        ...form,
        unidade_singular: form.unidade_singular.trim() || 'unidade',
        unidade_plural: form.unidade_plural.trim() || 'unidades',
        bloco_tamanho: tamanho,
    };
    const linha = calcularLinha(metrica, {bloqueia: true}, limite, apurado);
    const medicao = form.natureza === 'CONSUMO' ? 'Soma tudo o que foi consumido no ciclo.' : (MEDICAO[form.apuracao] || '');
    const avisos = form.avisos.length
        ? `A empresa é avisada ao chegar a ${form.avisos.map((aviso) => `${aviso}%`).join(' e ')} do limite.`
        : 'Sem avisos de consumo.';
    const licenca = form.ao_atingir === 'BLOQUEAR'
        ? 'Ao atingir o limite, o sistema bloqueia.'
        : 'Ao atingir o limite, o sistema deixa passar e o excedente é cobrado, se o plano tiver preço.';
    return {
        texto: `${medicao} ${licenca} ${avisos}`,
        origem: serve ? `Exemplo com os valores de ${referencia.nome}` : 'Exemplo com valores fictícios',
        nome: form.nome.trim() || 'Nova métrica',
        linha,
    };
};

/* ---------- apontamento no sistema ---------- */

// A regra da fonte do trigger da 0093: só usuários vem do backoffice, consumo chega por eventos e estado pela foto.
export const recusaDaFonte = (metrica, fonte) => {
    if (!metrica) return '';
    if (fonte === 'BACKOFFICE' && metrica.chave !== CHAVE_USUARIOS) {
        return `Só usuários é medida pelo backoffice, pelos vínculos. ${metrica.nome} vem do sistema.`;
    }
    if (metrica.natureza === 'CONSUMO' && fonte !== 'EVENTOS') return `${metrica.nome} é consumo: chega por eventos, o total de cada hora.`;
    if (metrica.natureza === 'ESTADO' && fonte === 'EVENTOS') return `${metrica.nome} é estado: chega pela foto diária, o valor do dia.`;
    return '';
};

export const fontesPermitidas = (metrica) => Object.keys(FONTES).filter((fonte) => !recusaDaFonte(metrica, fonte));

export const apontamentoVazio = () => ({metrica_id: '', fonte: '', como_mede: '', bloqueia: true, custo_referencia: ''});

export const formDoApontamento = (apontamento) => ({
    metrica_id: apontamento.metrica_id,
    fonte: apontamento.fonte || '',
    como_mede: apontamento.como_mede || '',
    bloqueia: apontamento.bloqueia !== false,
    custo_referencia: apontamento.custo_referencia == null ? '' : String(apontamento.custo_referencia),
});

export const validarApontamento = (form, metrica) => {
    if (!form.metrica_id) return 'Escolha a métrica.';
    if (!form.fonte) return 'Escolha de onde vem a medição.';
    const recusa = recusaDaFonte(metrica, form.fonte);
    if (recusa) return recusa;
    if (!form.como_mede.trim()) return 'Descreva como o sistema mede. É o que o time de produto confere quando a fatura for questionada.';
    if (!vazio(form.custo_referencia) && !(Number(form.custo_referencia) >= 0)) return 'O custo de referência é zero ou mais, ou fica vazio.';
    return '';
};

// Um item da lista `metricas` do PUT do sistema. A lista vai inteira: o que fica de fora deixa de estar apontado.
export const itemDoApontamento = (apontamento) => ({
    metrica_id: apontamento.metrica_id,
    fonte: apontamento.fonte,
    como_mede: (apontamento.como_mede || '').trim(),
    bloqueia: apontamento.bloqueia !== false,
    custo_referencia: vazio(apontamento.custo_referencia) ? null : Number(apontamento.custo_referencia),
});

export const listaComApontamento = (apontamentos = [], form) => {
    const item = itemDoApontamento(form);
    const existe = apontamentos.some((atual) => atual.metrica_id === item.metrica_id);
    return existe
        ? apontamentos.map((atual) => (atual.metrica_id === item.metrica_id ? item : itemDoApontamento(atual)))
        : [...apontamentos.map(itemDoApontamento), item];
};

export const listaSemApontamento = (apontamentos = [], metricaId) => (
    apontamentos.filter((atual) => atual.metrica_id !== metricaId).map(itemDoApontamento)
);

// As versões de plano deste sistema com limite da métrica.
export const versoesNoSistema = (metrica, sistemaId) => (metrica?.versoes || []).filter((versao) => versao.id_sistema === sistemaId);
